import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { formatMiles } from "../../../../utils/miles";

/**
 * ModalNewFactura
 * - Monto: SOLO enteros positivos (sin decimales). Se formatea en tiempo real con separador de miles.
 *
 * Props:
 *  - estado (boolean)
 *  - onCloseModal ()
 *  - onReturnValue ({ idusuario, numerofactura, monto, isNEW, idfacturasprofesionales? })
 *  - profesionales: [{ idusuario, usuario, nombre }]
 *  - isNEW (boolean) indica si es creación (true) o edición (false)
 *  - data (obj|null) cuando edita
 */
const ModalNewFactura = ({
  estado,
  onCloseModal,
  onReturnValue,
  profesionales = [],
  isNEW,
  data = null,
}) => {
  const initialForm = {
    idfacturasprofesionales: null,
    idusuario: "",
    numerofactura: "",
    montoRaw: "",      // Valor crudo solo dígitos: "123456"
    montoDisplay: "",  // Valor formateado: "123.456"
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const montoRef = useRef(null);

  // Cargar datos al abrir (editar) o limpiar (nuevo)
  useEffect(() => {
    if (data && !isNEW) {
      setForm({
        idfacturasprofesionales: data.idfacturasprofesionales ?? null,
        idusuario: String(data.idusuario ?? ""),
        numerofactura: data.numerofactura ?? "",
        montoRaw: String(data.monto ?? ""),
        montoDisplay:
          data.monto != null && data.monto !== ""
            ? formatMiles(Number(data.monto))
            : "",
      });
    } else {
      setForm(initialForm);
    }
  }, [data, isNEW]);

  useEffect(() => {
    if (estado && isNEW) {
      setForm(initialForm);
      setErrors({});
    }
  }, [estado, isNEW]);

  // Helpers
  const toNumber = (raw) => {
    if (!raw) return 0;
    return Number(raw);
  };

  // Manejo y formateo en tiempo real SOLO ENTEROS POSITIVOS
  const handleMontoChange = (e) => {
    const input = e.target;
    const original = input.value;
    const caret = input.selectionStart || 0;

    // Extraer solo dígitos
    let digitsOnly = original.replace(/\D/g, "");

    // Evitar múltiples ceros al inicio
    if (digitsOnly.length > 1) {
      digitsOnly = digitsOnly.replace(/^0+/, "");
      if (digitsOnly === "") digitsOnly = "0";
    }

    const formatted = digitsOnly === "" ? "" : formatMiles(Number(digitsOnly));

    // Contar dígitos antes del caret en el valor ORIGINAL
    const digitsBeforeCaret = original.slice(0, caret).replace(/\D/g, "").length;

    setForm((prev) => ({
      ...prev,
      montoRaw: digitsOnly,
      montoDisplay: formatted,
    }));

    // Restaurar caret
    requestAnimationFrame(() => {
      if (!montoRef.current) return;
      let newPos = formatted.length;

      let count = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) count++;
        if (count === digitsBeforeCaret) {
          newPos = i + 1;
          break;
        }
      }
      try {
        montoRef.current.setSelectionRange(newPos, newPos);
      } catch {
        // ignorar si el input no tiene foco
      }
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "monto") {
      handleMontoChange(e);
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.idusuario) e.idusuario = "Selecciona un profesional";
    if (!form.numerofactura) e.numerofactura = "Número requerido";

    if (form.montoRaw === "") {
      e.monto = "Monto requerido";
    } else {
      const num = toNumber(form.montoRaw);
      if (isNaN(num)) e.monto = "Monto inválido";
      else if (num <= 0) e.monto = "Debe ser > 0";
    }
    return e;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    const num = toNumber(form.montoRaw);

    const payload = {
      idusuario: Number(form.idusuario),
      numerofactura: form.numerofactura.trim(),
      monto: num,
      isNEW: !!isNEW,
    };

    // Solo enviar idfacturasprofesionales cuando edita
    if (!isNEW && form.idfacturasprofesionales != null) {
      payload.idfacturasprofesionales = form.idfacturasprofesionales;
    }

    onReturnValue(payload);
    onCloseModal();
  };

  return (
    <Modal show={estado} onHide={onCloseModal} backdrop="static" centered size="md">
      <Form id="newFacturaForm" onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>
            {isNEW ? "Nueva Factura Profesional" : "Editar Factura Profesional"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pb-2">
          {/* Profesional */}
          <Form.Group className="mb-3" controlId="factura-profesional">
            <Form.Label>
              Profesional <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              name="idusuario"
              value={form.idusuario}
              onChange={onChange}
              isInvalid={!!errors.idusuario}
            >
              <option value="">Seleccione...</option>
              {Array.isArray(profesionales) &&
                profesionales.map((p) => (
                  <option key={p.idusuario} value={String(p.idusuario)}>
                    {p.idusuario} - {p.usuario || p.nombre || "SinUsuario"}
                  </option>
                ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.idusuario}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Número de Factura */}
          <Form.Group className="mb-3" controlId="factura-numero">
            <Form.Label>
              Número de Factura <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="numerofactura"
              value={form.numerofactura}
              onChange={onChange}
              placeholder="Ej: 001-001-2025"
              maxLength={40}
              isInvalid={!!errors.numerofactura}
              autoComplete="off"
            />
            <Form.Control.Feedback type="invalid">
              {errors.numerofactura}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Monto (solo entero positivo) */}
          <Form.Group className="mb-2" controlId="factura-monto">
            <Form.Label>
              Monto <span className="text-danger">*</span>
              <small className="text-muted ms-1">(entero &gt; 0)</small>
            </Form.Label>
            <Form.Control
              ref={montoRef}
              type="text"
              name="monto"
              value={form.montoDisplay}
              onChange={onChange}
              isInvalid={!!errors.monto}
              placeholder="0"
              autoComplete="off"
              inputMode="numeric"
            />
            <Form.Control.Feedback type="invalid">
              {errors.monto}
            </Form.Control.Feedback>

            {form.montoRaw && !errors.monto && (
              <div className="form-text">
                Valor numérico: {toNumber(form.montoRaw).toLocaleString("es-ES")}
              </div>
            )}
          </Form.Group>

          <div className="small text-muted">
            Verifica que el número de factura no exista previamente antes de guardar.
          </div>
        </Modal.Body>

        <Modal.Footer className="py-2">
          <Button variant="danger" onClick={onCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit">
            {isNEW ? "Guardar" : "Actualizar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalNewFactura;
